import type { MockMessage } from "@/dummy";

/**
 * What the visitor did in the DEMO conversation, per thread, for the lifetime of the page. The
 * messages tab remounts each time the visitor leaves a thread, and the demo has no backend to
 * hand the exchange back, so without this their sent message and the canned reply vanished.
 * Written through when a message is appended, so a canned reply that lands after the tab was left
 * is still kept.
 */
interface DemoSession {
  appended: MockMessage[];
  consumed: string[];
  read: string[];
}

const sessions = new Map<string, DemoSession>();

function sessionOf(key: string): DemoSession {
  let s = sessions.get(key);
  if (!s) {
    s = { appended: [], consumed: [], read: [] };
    sessions.set(key, s);
  }
  return s;
}

export function loadDemoSession(key: string): DemoSession {
  const s = sessions.get(key);
  return s
    ? { appended: [...s.appended], consumed: [...s.consumed], read: [...s.read] }
    : { appended: [], consumed: [], read: [] };
}

export function rememberDemoMessage(key: string, msg: MockMessage): void {
  const s = sessionOf(key);
  if (!s.appended.some((m) => m.id === msg.id)) s.appended = [...s.appended, msg];
}

export function rememberDemoSets(key: string, consumed: Set<string>, read: Set<string>): void {
  const s = sessionOf(key);
  s.consumed = [...consumed];
  s.read = [...read];
}

/** Test helper. */
export function clearDemoSessions(): void {
  sessions.clear();
}
